import { Component, DestroyRef, inject,  EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EMPTY_GUID } from '@core/constants';
import { AuthorizationCertPeriod, AuthorizationRequest, CertPeriodRangeCalcDTO, OrderSubType, MemberSearch } from '@core/model';
import {
  AuthenticationService,
  AuthorizationRequestService,
  AuthorizationService,
  LookupService,
  TenantService
} from '@core/service';
import { Requestors, RequestorsDropdownMap } from '@shared/dropdowns';
import moment from 'moment';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { combineLatest, Observable } from 'rxjs';
import { DateUtilities, noWhiteSpaceValidator, SecurityService, TenantNames } from '@mynexus/mynexus-ui-lib';
import { ORDER_SUBTYPES } from '@shared/constants';
import { RequestSources, RequestSourcesDropdown } from '@shared/enumeration';
import { minDateValidator } from '@shared/validators';
import { InboundFaxService } from '@core/service/inbound-fax.service';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-auth-request-authorization',
    templateUrl: './auth-request-authorization.component.html',
    styleUrls: ['./auth-request-authorization.component.scss'],
    standalone: false
})
export class AuthRequestAuthorizationComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private displaySocDialogValue: boolean;
  private socConfirmedValue: boolean;
  @Output() isStepFormValid = new EventEmitter<boolean>();
  @Output() socWasConfirmed = new EventEmitter<boolean>();
  @Output() authRequestChanged = new EventEmitter<AuthorizationRequest>();
  @Output() memberRepresentativeNameChange = new EventEmitter<string>();

  @Input() requestType: string;
  @Input() authRequest: AuthorizationRequest;
  @Input() contractStartDate: string;
  @Input() memberSearch: MemberSearch;
  @Input() set socConfirmed(value: boolean) {
    this.socConfirmedValue = value;
  }
  get socConfirmed(): boolean {
    return this.socConfirmedValue;
  }
  @Input() set displaySocDialog(value: boolean) {
    this.displaySocDialogValue = value;
    this.createConfirmationForm();
    if (value) {
      this.socConfirmationForm
        .get('startOfCareDate')
        .setValue(new Date(moment(this.authRequest.authorizationDTO.startOfCareDate, 'L').format('l')));
    }
  }
  get displaySocDialog(): boolean {
    return this.displaySocDialogValue;
  }

  @Input() memberRepresentativeName: string;
  @Input() clearSocDate: Observable<boolean>;
  @Input() hh30DayReviewPeriods = false;

  tenantId: string;
  isLoading = false;
  isExternalUser: boolean
  referralRequired = false;
  isSocLoading = false;
  socDateEntered: string = null;
  emptyGuid = EMPTY_GUID;
  maxDate: Date = new Date();
  private readonly socMaxDaysWindow = 6;
  maxSocDate: Date = this.getMaxSocDate();
  private previousDtrrDate: string = null;
  minFacilityDischargeDate: Date;

  authorizationForm: FormGroup;
  canUpdateRequestSource: boolean;
  subOrderTypes: SelectItem[];
  urgencies: SelectItem[];
  orderTypes: SelectItem[];
  referralSources: SelectItem[];
  requestors: SelectItem[];
  requestSources: SelectItem[];
  hasContractFlow: boolean;
  socConfirmationForm: FormGroup;
  displayRequestorRepresentativeNameDialog = false;
  isImmediateContactDetailsFormValid = true;
  setDateTimeFaxReceivedReadonly = false;

  constructor(
    route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private authorizationRequestService: AuthorizationRequestService,
    private authorizationService: AuthorizationService,
    private lookupService: LookupService,
    private confirmationService: ConfirmationService,
    private tenantsService: TenantService,
    securityService: SecurityService,
    private readonly inboundFaxService: InboundFaxService
  ) {
    this.canUpdateRequestSource = securityService.hasAccess(['Authorization.RequestSource.Update']);
    this.isExternalUser = authenticationService.isExternalUser;
    this.tenantId = route.snapshot.params.tenantId;
  }

  ngOnInit() {
    // Get Lookup Values and Build the Form
    this.getLookups();
    this.buildAuthorizationForm();

    this.clearSocDate.subscribe({
      next: value => {
        if (value && this.authorizationForm) {
          this.authorizationForm.get('startOfCareDate').setValue('');
          this.authorizationForm.get('startOfCareDate').updateValueAndValidity();
          this.handleStartOfCareDateChanges(null);
        }
      }
    });

    // Form Subscriptions
    this.authorizationFormWatch();
    this.referralRequiredWatch();
    this.urgencyWatch();
    this.startOfCareAndDateTimeFaxReceivedWatch();

    // Set Any Default Values
    this.setDefaultValues();
    this.initReferralRequired();

    this.inboundFaxService.inboundFaxData$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(inboundFax => {
      if (inboundFax) {
        this.authorizationForm.get('dateTimeFaxReceivedTime').setValue(new Date(inboundFax.faxReceivedDateTime));
        this.authorizationForm.get('dateTimeFaxReceivedDate').setValue(new Date(inboundFax.faxReceivedDateTime));
        this.setDateTimeFaxReceivedReadonly = true;
      }
    });

    // Initial Form Validation Emit
    this.isStepFormValid.emit(this.authorizationForm.valid && this.isImmediateContactDetailsFormValid);
  }

  ngOnDestroy() {
  }

  initReferralRequired() {
    if (this.isExternalUser) {
      const rs = this.authorizationForm.get('referalSource').value;
      const rn = this.authorizationForm.get('requestorName').value;

      if (rs) {
        this.referralRequired = (
          rs.toLowerCase() === 'snf - skilled nursing facility' || rs === 'INP - Hospital' ||
          rn === 'SNF' || rn === 'Hospital');

        if (this.referralRequired) {
          this.authorizationForm.get('referralName').setValidators([Validators.required, Validators.maxLength(50)]);
          this.authorizationForm.get('facilityDischargeDate').setValidators([Validators.required, minDateValidator(this.minFacilityDischargeDate)]);
        } else {
          this.authorizationForm.get('referralName').setValidators([Validators.maxLength(50)]);
          this.authorizationForm.get('facilityDischargeDate').clearValidators();
        }
        this.authorizationForm.get('referralName').updateValueAndValidity();
        this.authorizationForm.get('facilityDischargeDate').updateValueAndValidity();
      }
    }
  } // initReferralRequired()

  setGlobalVariables() {
    this.hasContractFlow = this.checkContractStartDate();
  }

  buildAuthorizationForm() {
    let facilityDischargeDate: Date;
    let startOfCareDate: string;

    if (this.authRequest.authorizationDTO.facilityDischargeDate === null) {
      facilityDischargeDate = null;
    } else {
      facilityDischargeDate = new Date(this.authRequest.authorizationDTO.facilityDischargeDate);
      facilityDischargeDate.setHours(0);
    }

    if (this.authRequest.authorizationDTO.startOfCareDate) {
      startOfCareDate = this.authRequest.authorizationDTO.startOfCareDate;
      this.setGlobalVariables();
    }


    if (this.authRequest.authorizationCertPeriodDTOs.length === 0 && startOfCareDate) {
      this.handleStartOfCareDateChanges(new Date(startOfCareDate));
    }

    const startOfCareDateValue: Date =
      this.authRequest.authorizationDTO.startOfCareDate &&
      moment(this.authRequest.authorizationDTO.startOfCareDate).local().toDate();

    const effectiveFromDate = this.authRequest.authorizationDTO?.effectiveFromDate
      ? moment(this.authRequest.authorizationDTO.effectiveFromDate)
      : null;

    const effectiveToDate = this.authRequest.authorizationDTO?.effectiveToDate
      ? moment(this.authRequest.authorizationDTO.effectiveToDate)
      : null;
    const certPeriodRange =
      effectiveFromDate && effectiveToDate
        ? `${effectiveFromDate.format(
          'MM/DD/yyyy'
        )} - ${effectiveToDate.format('MM/DD/yyyy')}`
        : '';

    const dateTimeFaxReceived: Date =
      this.authRequest.authorizationDTO.dateTimeFaxReceived &&
      moment(moment.utc(this.authRequest.authorizationDTO.dateTimeFaxReceived)).local().toDate();

    this.authorizationForm = new FormGroup({
      orderType: new FormControl(this.authRequest.authorizationDTO.orderType, Validators.required),
      subType: new FormControl(this.authRequest.authorizationDTO.orderSubType, Validators.required),
      urgency: new FormControl(this.authRequest.authorizationDTO.urgency, Validators.required),
      referalSource: new FormControl(this.authRequest.authorizationDTO.referralSource, Validators.required),
      referralName: new FormControl(this.authRequest.authorizationDTO.referralName, Validators.maxLength(50)),
      referringFacilityNPI: new FormControl(this.authRequest.authorizationDTO.referringFacilityNPI, [
        Validators.maxLength(10), Validators.minLength(10), Validators.pattern('[0-9]*'), noWhiteSpaceValidator
      ]),
      facilityDischargeDate: new FormControl({value: facilityDischargeDate, disabled: !startOfCareDateValue}),
      dateTimeFaxReceivedDate: new FormControl(dateTimeFaxReceived, Validators.required),
      dateTimeFaxReceivedTime: new FormControl(dateTimeFaxReceived, Validators.required),
      startOfCareDate: new FormControl(startOfCareDateValue, Validators.required),
      requestorName: new FormControl(this.authRequest.authorizationDTO.requestorName, Validators.required),
      requestSource: new FormControl({ value: this.authRequest.authorizationDTO.requestSource, disabled: !this.canUpdateRequestSource }, Validators.required),
      memberRepresentative: new FormControl(this.memberRepresentativeName),
      certPeriod: new FormControl({ value: certPeriodRange, disabled: true }),
    });

    if (startOfCareDateValue)
      this.setMinimumFacilityDischargeDate(startOfCareDateValue);

    if (this.isExternalUser) {
      this.authorizationForm.get('requestSource').setValue(RequestSources.External)
    }
  }

  get requestorName(): AbstractControl {
    return this.authorizationForm.get('requestorName');
  }

  get memberRepresentative(): AbstractControl {
    return this.authorizationForm.get('memberRepresentative');
  }

  getLookups() {
    this.isLoading = true;
    this.lookupService.getOrderSubTypes(this.tenantId, !this.isExternalUser)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(r => {
      let f: OrderSubType[];
      if (this.isExternalUser) {
        f = r.filter(p => p.active === true && p.isExternal === true);
      } else {
        f = r.filter(p => p.active === true && p.isInternal === true);
      }
      const isAnthemTenant = this.tenantsService.getTenantById(this.tenantId)?.name === TenantNames.Anthem;
      this.subOrderTypes = f.reduce((acc, p) => {
      if (
        ((isAnthemTenant && !this.isExternalUser) || p.orderSubType !== ORDER_SUBTYPES.LEVEL_OF_PAC)
        && p.orderSubType !== ORDER_SUBTYPES.PAC_DISCHARGE)
      {
        acc.push({ value: p.orderSubType, label: p.orderSubType } as SelectItem)
      }

      return acc;
    }, [] as SelectItem[]);
    });

    this.lookupService.getTenantUrgencies(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(r => {
      this.urgencies = r
        .filter(e => e.isAuthRequest)
        .map(t => ({ value: t.urgency, label: t.urgency } as SelectItem));
    });

    this.lookupService.getOrderTypes(this.tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(r => {
      this.isLoading = false;
      this.orderTypes = r.map(p => ({ value: p.orderType, label: p.orderType } as SelectItem));
    });

    this.lookupService.getReferralSources(!this.isExternalUser)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(r => {
      this.referralSources = r.map(p => ({ value: p.referralSource, label: p.referralSource } as SelectItem));
    });

    this.loadRequestors();
    this.loadReuqestSources();
  }

  loadReuqestSources() {
    this.requestSources = RequestSourcesDropdown
      .map((value) => ({ value, label: value }) as SelectItem);
  }

  loadRequestors() {
    const activeTenant = this.tenantsService.getTenantById(this.tenantId);
    const tenantName = activeTenant ? activeTenant.name : '';

    this.requestors = (RequestorsDropdownMap[tenantName] || Requestors)
      .map((value) => ({ value, label: value }) as SelectItem);
    // Remove 'Member', 'Member Representative' and 'CareBridge' from the dropdown for external users
    if (this.isExternalUser) {
      this.requestors = this.requestors.filter(r => !['Member', 'Member Representative', 'CareBridge'].includes(r.value));
    }
  }

  setDefaultValues() {
    if (!this.authRequest.authorizationDTO.orderType) {
      // Order Type
      // this.authRequest.orderType = 'Home Health Care';
      this.authorizationForm.controls['orderType'].setValue('Home Health Care');
      if (this.isExternalUser) {
        this.authorizationForm.get('orderType').disable();
        this.authorizationForm.get('dateTimeFaxReceivedDate').clearValidators();
        this.authorizationForm.get('dateTimeFaxReceivedTime').clearValidators();
        this.authorizationForm.get('dateTimeFaxReceivedDate').updateValueAndValidity();
        this.authorizationForm.get('dateTimeFaxReceivedTime').updateValueAndValidity();
        this.authRequest.authorizationDTO.dateTimeFaxReceived = this.parseDateToUtc(new Date());
        // The following display the local current date/time when date has not been returned from the db.
        if (this.authRequest.authorizationDTO.dateTimeFaxReceived.endsWith('Z')) {
          this.authRequest.authorizationDTO.dateTimeFaxReceived = this.authRequest.authorizationDTO.dateTimeFaxReceived.slice(0, -1);
        }

        this.authorizationForm.get('dateTimeFaxReceivedDate').setValue(moment(moment.utc(this.authRequest.authorizationDTO.dateTimeFaxReceived)).local().toDate());
        this.authorizationForm.get('dateTimeFaxReceivedTime').setValue(moment(moment.utc(this.authRequest.authorizationDTO.dateTimeFaxReceived)).local().toDate());
      }

      this.authorizationForm.controls['orderType'].updateValueAndValidity();

      this.authRequestChanged.emit(this.authRequest);
    }
  }

  // Subscribe to Form
  authorizationFormWatch(): void {
    this.authorizationForm.valueChanges.subscribe(val => {
      this.isStepFormValid.emit(this.authorizationForm.valid && this.isImmediateContactDetailsFormValid);
      this.loadAuthRequestFieldChanges(this.authorizationForm.getRawValue());
      this.authRequestChanged.emit(this.authRequest);
    });
    this.memberRepresentative.valueChanges.subscribe(res => {
      if (this.memberRepresentativeName !== res) {
        this.memberRepresentativeNameChange.emit(res);
      }
    })
  }

  authRequestUpdated(authRequest: AuthorizationRequest) {
    this.authRequest.authorizationDTO.contactName = authRequest.authorizationDTO.contactName;
    this.authRequest.authorizationDTO.phone = authRequest.authorizationDTO.phone;
    this.authRequest.authorizationDTO.phoneExt = authRequest.authorizationDTO.phoneExt;
    this.authRequest.authorizationDTO.fax = authRequest.authorizationDTO.fax;
    this.authRequestChanged.emit(this.authRequest);
  }
  immediateContactDetailsFormValid(isImmediateContactDetailsFormValid: boolean) {
    this.isImmediateContactDetailsFormValid = isImmediateContactDetailsFormValid;
    this.isStepFormValid.emit(this.authorizationForm.valid && this.isImmediateContactDetailsFormValid);
  }

  referralRequiredWatch() {
    this.authorizationForm.get('referalSource')
      .valueChanges.subscribe(v => this.setReferralRequired(v));
    this.requestorName
      .valueChanges.subscribe(v => {
        this.setReferralRequired(v);
        if (v === 'Member Representative') {
          this.displayRequestorRepresentativeNameDialog = true;
          this.memberRepresentative.setValidators([Validators.required, Validators.maxLength(25)]);
        } else {
          this.memberRepresentative.setValue(null);
          this.memberRepresentative.clearValidators();
          this.memberRepresentative.updateValueAndValidity();
        }
      });
  }

  setReferralRequired(value: string) {
    if (this.isExternalUser) {
      const rs = this.authorizationForm.get('referalSource').value;
      const rn = this.requestorName.value;

      if (value) {
        this.referralRequired = (
          rs && rs.toLowerCase() === 'snf - skilled nursing facility' || rs === 'INP - Hospital' ||
          rn === 'SNF' || rn === 'Hospital');

        if (this.referralRequired) {
          this.authorizationForm.get('referralName').setValidators([Validators.required, Validators.maxLength(50)]);
          this.authorizationForm.get('facilityDischargeDate').setValidators([Validators.required, minDateValidator(this.minFacilityDischargeDate)]);
        } else {
          this.authorizationForm.get('referralName').setValidators([Validators.maxLength(50)]);
          this.authorizationForm.get('facilityDischargeDate').clearValidators();
        }
        this.authorizationForm.get('referralName').updateValueAndValidity();
        this.authorizationForm.get('facilityDischargeDate').updateValueAndValidity();
      }
    } else {
      this.authorizationForm.get('referralName').setValidators([Validators.maxLength(50)]);
      this.authorizationForm.get('facilityDischargeDate').clearValidators();
      this.authorizationForm.get('referralName').updateValueAndValidity();
      this.authorizationForm.get('facilityDischargeDate').updateValueAndValidity();
    }
  }

  urgencyWatch() {
    this.authorizationForm.get('urgency')
      .valueChanges.subscribe(v => this.setUrgencyAcknowledgement(v));
  }

  setUrgencyAcknowledgement(value: string) {
    if (this.isExternalUser && String(value).toLowerCase() === 'urgent') {
      this.showUrgencyAcknowledgementMessage();
    }
  }

  showUrgencyAcknowledgementMessage() {
    this.confirmationService.confirm({
      message: `You are submitting this request as urgent and thereby attesting to the CMS definition
          for expedited decisions.In doing so, you are indicating that processing the request
          under a standard timeframe could place the enrollee's life, health or ability
          to regain maximum function in jeopardy.Please revise if appropriate.`,
      header: 'Acknowledgement',
      accept: () => { }
    });
  }

  private getCertPeriodDateRange(effectiveFromDate: string): void {
    if (!this.authorizationForm.get('dateTimeFaxReceivedDate').value)
      return;

    this.isSocLoading = true;
    this.authorizationRequestService.getCertPeriodRangeFromEffectiveFromDate(effectiveFromDate, this.authorizationForm.get('dateTimeFaxReceivedDate').value)
    .pipe(takeUntilDestroyed(this.destroyRef))  
    .subscribe(certPeriod => {
        this.isSocLoading = false;
        this.setCertPeriod(certPeriod);
      });
  }

  private getCertPeriodRangeForContractStartDate(startOfCareDate: Date) {
    this.authorizationService.getCertPeriodRangeForContractStartDate(this.contractStartDate, startOfCareDate, this.authorizationForm.get('dateTimeFaxReceivedDate').value)
      .subscribe((certPeriods) => {
        this.isSocLoading = false;

        if (certPeriods) {
          this.setCertPeriod(certPeriods[0]);
        }
      });
  }


  private setCertPeriod(certPeriod: CertPeriodRangeCalcDTO) {
    let certPeriodRange = '';

    if (certPeriod != null) {
      const effectiveFromDate = moment(certPeriod.effectiveFromDate);
      const effectiveToDate = moment(certPeriod.effectiveToDate);

      this.setAuthEffectiveDates(effectiveFromDate.format('L'), effectiveToDate.format('L'));
      this.setAuthCertPeriod(certPeriod);
      certPeriodRange = `${effectiveFromDate.format('MM/DD/yyyy')} - ${effectiveToDate.format('MM/DD/yyyy')}`;
    }

    if (this.authorizationForm) {
      this.authorizationForm.get('certPeriod').setValue(certPeriodRange);
      this.authorizationForm.get('certPeriod').updateValueAndValidity();
    }
  }

  setAuthCertPeriod(certPeriod: CertPeriodRangeCalcDTO) {
    if (this.authRequest.authorizationCertPeriodDTOs && this.authRequest.authorizationCertPeriodDTOs.length === 0) {
      const acp = new AuthorizationCertPeriod();
      if (this.hh30DayReviewPeriods) {
        for (let index = 0; index < certPeriod.reviewPeriods.length; index++) {
          // TODO: Refactor out of here.
          const reviewPeriod = certPeriod.reviewPeriods[index];

          reviewPeriod.createdBy = this.authenticationService.claims.emails[0];
        acp.reviewPeriods = certPeriod.reviewPeriods;
        }
      }
      acp.authorizationCertPeriodId = this.emptyGuid;
      acp.authorizationId = this.authRequest.authorizationDTO.authorizationId;
      acp.certPeriod = 1;
      acp.recertDate = moment(certPeriod.effectiveFromDate).format('L');
      acp.effectiveFromDate = moment(certPeriod.effectiveFromDate).format('L');
      acp.effectiveToDate = moment(certPeriod.effectiveToDate).format('L');
      acp.isLocked = false;
      acp.createdBy = this.authenticationService.claims.emails[0];
      acp.modifiedBy = this.authenticationService.claims.emails[0];
      acp.createdOn = moment().utc().toISOString();
      acp.modifiedOn = moment().utc().toISOString();
      this.authRequest.authorizationCertPeriodDTOs.push(acp);
    } else {
      this.authRequest.authorizationCertPeriodDTOs[0].effectiveFromDate = moment(certPeriod.effectiveFromDate).format('L');
      this.authRequest.authorizationCertPeriodDTOs[0].effectiveToDate = moment(certPeriod.effectiveToDate).format('L');
      this.authRequest.authorizationCertPeriodDTOs[0].recertDate = moment(certPeriod.effectiveFromDate).format('L');
      this.authRequest.authorizationCertPeriodDTOs[0].modifiedBy = this.authenticationService.claims.emails[0];
      this.authRequest.authorizationCertPeriodDTOs[0].modifiedOn = moment().utc().toISOString();

      if (this.hh30DayReviewPeriods) {
        for (let index = 0; index < certPeriod.reviewPeriods.length; index++) {
          // TODO: Refactor out of here.
          const reviewPeriod = certPeriod.reviewPeriods[index];

          reviewPeriod.createdBy = this.authenticationService.claims.emails[0];
        }

        this.authRequest.authorizationCertPeriodDTOs[0].reviewPeriods = certPeriod.reviewPeriods;
      }
    }
    this.authRequestChanged.emit(this.authRequest);
  }

  handleStartOfCareDateChanges(newSOC: Date) {
    if (newSOC) {
      newSOC.setHours(0);
      this.authRequest.authorizationDTO.startOfCareDate = moment(newSOC).format('L');
      this.setGlobalVariables();

      // With Project Chameleon, we need to have  the DTRR Date in order to apply logic for the cert period ranges.
      if (this.authorizationForm.get('dateTimeFaxReceivedDate').value) {
        if (this.hasContractFlow) {
          this.getCertPeriodRangeForContractStartDate(newSOC);
        } else {
          this.getCertPeriodDateRange(`${newSOC.getMonth() + 1}-${newSOC.getDate()}-${newSOC.getFullYear()}`);
        }
      }

      this.setMinimumFacilityDischargeDate(newSOC);
      this.authorizationForm?.get('facilityDischargeDate').enable();
    } else if (this.authorizationForm) {
      this.authorizationForm.get('certPeriod').setValue('');
      this.authorizationForm.get('certPeriod').updateValueAndValidity();
      this.authorizationForm.get('facilityDischargeDate').disable();
      this.socWasConfirmed.emit(false);
    }
  }

  setAuthEffectiveDates(fromDate: string, toDate: string) {
    this.authRequest.authorizationDTO.effectiveFromDate = fromDate;
    this.authRequest.authorizationDTO.recertDate = fromDate;
    this.authRequest.authorizationDTO.effectiveToDate = toDate;
    this.authRequestChanged.emit(this.authRequest);
  }

  loadAuthRequestFieldChanges(formValue) {
    this.authRequest.authorizationDTO.orderType = formValue.orderType;
    this.authRequest.authorizationDTO.orderSubType = formValue.subType;
    this.authRequest.authorizationDTO.urgency = formValue.urgency;
    this.authRequest.authorizationDTO.referralSource = formValue.referalSource;
    this.authRequest.authorizationDTO.referralName = formValue.referralName;
    this.authRequest.authorizationDTO.referringFacilityNPI = formValue.referringFacilityNPI;
    if ((formValue.facilityDischargeDate === undefined || formValue.facilityDischargeDate) === null) {
      this.authRequest.authorizationDTO.facilityDischargeDate = null;
    } else {
      const fdd = new Date(formValue.facilityDischargeDate);
      this.authRequest.authorizationDTO.facilityDischargeDate = fdd.toISOString();
    }

    this.authRequest.authorizationDTO.requestorName = formValue.requestorName;
    this.authRequest.authorizationDTO.requestSource = formValue.requestSource;

    if (!this.isExternalUser) {
      if (formValue.dateTimeFaxReceivedDate !== undefined && formValue.dateTimeFaxReceivedDate !== null &&
        formValue.dateTimeFaxReceivedTime !== undefined && formValue.dateTimeFaxReceivedTime !== null) {
        this.maxDate = new Date();  // Reset max date
        const dt: Date = formValue.dateTimeFaxReceivedDate;
        const tm: Date = formValue.dateTimeFaxReceivedTime;

        dt.setHours(tm.getHours(), tm.getMinutes(), tm.getSeconds());

        // Date/Time cannot be greater than the current date/time
        if (moment(dt).format('L') === moment(this.maxDate).format('L')) {
          if (moment(this.maxDate, 'h:mm:ss a').isBefore(moment(tm, 'h:mm:ss a'))) {
            dt.setHours(this.maxDate.getHours(), this.maxDate.getMinutes(), this.maxDate.getSeconds());
            this.authorizationForm.get('dateTimeFaxReceivedTime').setValue(dt);
            this.authorizationForm.get('dateTimeFaxReceivedTime').updateValueAndValidity();
          }
        }

        this.authRequest.authorizationDTO.dateTimeFaxReceived = moment.utc(dt).toISOString();

        const currentDtrrDate = moment(formValue.dateTimeFaxReceivedDate).format('L');
        if (this.previousDtrrDate && this.previousDtrrDate !== currentDtrrDate) {
          this.authorizationForm.get('startOfCareDate').setValue(null);
        }
        this.previousDtrrDate = currentDtrrDate;
        this.maxSocDate = this.getMaxSocDate(dt);
      }
    }

  }

  handleUrgencyChange(urgency: string) {
    if (this.authRequest && this.authRequest.authorizationDetailDTOs) {
      this.authRequest.authorizationDetailDTOs.forEach(p => p.urgency = urgency);
    }
  }

  parseDateToUtc(value: any) { return value ? new Date(value).toUTCString() : null; }

  private checkContractStartDate(): boolean {
    const contractStartDate = this.contractStartDate;
    const startOfCareDate = moment(this.authRequest.authorizationDTO.startOfCareDate, 'L');

    return !!contractStartDate && startOfCareDate.isBefore(contractStartDate);
  }

  handleStartOfCareDateConfirmation() {
    const startOfCareDate = this.authRequest.authorizationDTO.startOfCareDate;
    this.displaySocDialog = false;
    if (this.authorizationForm) {
      this.authorizationForm.get('startOfCareDate').setValue(startOfCareDate);
      this.authorizationForm.get('startOfCareDate').updateValueAndValidity();
    }
    if (this.authRequest.authorizationDTO.startOfCareDate) {
      this.socDateEntered = moment(new Date(startOfCareDate).setHours(0)).format('L');
    }

    if (this.socDateEntered) {
      this.authRequestChanged.emit(this.authRequest);
    }
    this.socWasConfirmed.emit(true);
  }

  createConfirmationForm() {
    this.socConfirmationForm = new FormGroup({
      startOfCareDate: new FormControl()
    });
    this.socConfirmationFormWatch();
  }

  socConfirmationFormWatch(): void {
    this.socConfirmationForm.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ startOfCareDate }) => {
      if (startOfCareDate) {
        this.socDateEntered = moment(new Date(startOfCareDate).setHours(0)).format('L');
      }
    });
  }

  private getMaxSocDate(baseDate: Date = new Date()): Date {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + this.socMaxDaysWindow);
    return d;
  }

  private setMinimumFacilityDischargeDate(startOfCareDate: Date): void {
    this.minFacilityDischargeDate = new Date(startOfCareDate);
    this.minFacilityDischargeDate.setDate(startOfCareDate.getDate() - 180);
    this.minFacilityDischargeDate = new Date (DateUtilities.setDateOnlyValue(this.minFacilityDischargeDate));
    this.authorizationForm.get('facilityDischargeDate').setValidators(minDateValidator(this.minFacilityDischargeDate));

    if (this.referralRequired)
      this.authorizationForm.get('facilityDischargeDate').addValidators(Validators.required);

    this.authorizationForm.get('facilityDischargeDate').updateValueAndValidity();
  }

  private startOfCareAndDateTimeFaxReceivedWatch(): void {
    combineLatest({
      startOfCare: this.authorizationForm.get('startOfCareDate').valueChanges,
      dateTimeFaxReceived: this.authorizationForm.get('dateTimeFaxReceivedDate').valueChanges
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ startOfCare, dateTimeFaxReceived }) => {
      if (startOfCare && dateTimeFaxReceived) {
        this.handleStartOfCareDateChanges(new Date(startOfCare));
      }
    });
  }
}
